import React from 'react';
import { ArrowRightIcon, ArrowDownIcon, PlusIcon, UsersIcon, HeartIcon, HandshakeIcon, TrendingUpIcon } from 'lucide-react';
import { usePick } from './StoryShared';
import { act1, act2, act4, act5, LOGO_ATNA } from '../../data/storyPage';

/* 400 ───→ 5 : the crowd thins out along one line */
export function QueueLine() {
  const pick = usePick();
  const {
    before,
    after,
    afterNote
  } = act1;
  const crowd = Array.from({
    length: 40
  });
  return <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-center gap-6 md:gap-10" data-id="element-6767" data-mp="12i5pdyiyrlq5">
      <div data-id="element-6768" data-mp="xggbmb4cwtyd">
        <p className="font-serif text-[5rem] lg:text-[7rem] leading-none tracking-tight text-[#9AA0B4]" data-id="element-6769" data-mp="xjvod7ak5qwm">{before.value}</p>
        <p className="mt-2 font-sans text-[15px] font-medium text-[#5A6178]" data-id="element-6770" data-mp="ohcpszdt20g6">{pick(before.label)}</p>
        <p className="font-sans text-[15px] text-[#8A8FA3]" data-id="element-6771" data-mp="2ntxcjq7ie9b9">{pick(before.sub)}</p>
      </div>

      <div className="relative h-16 md:h-24" aria-hidden="true" data-id="element-6772" data-mp="2iv45ogmq9gk2">
        <div className="absolute inset-x-0 top-1/2 h-px bg-[#DCD9FA]" data-id="element-6773" data-mp="2ivvr4ymm1xwm" />
        <div className="absolute inset-0 hidden md:flex items-center" data-id="element-6774" data-mp="a6mk6iqwkegq">
          {crowd.map((_, i) => {
          const t = i / (crowd.length - 1);
          const keep = t < 0.55 || i % Math.round(1 + t * 6) === 0;
          if (!keep) return <span key={i} className="flex-1" data-id="element-6775" data-mp="192b4nmstdvk" />;
          return <span key={i} className="flex-1 flex justify-center" data-id="element-6776" data-mp="66vmdv2yky2p" data-mpi={i}>
                <span className="w-2 h-2 rounded-full" style={{
              backgroundColor: t > 0.8 ? '#4F46E5' : '#B9BDCB',
              opacity: 1 - t * 0.35,
              transform: `translateY(${i * 7 % 5 - 2}px)`
            }} data-id="element-6777" data-mp="tv534ilbevmz" />
              </span>;
        })}
        </div>
        <ArrowRightIcon className="hidden md:block absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4F46E5]" strokeWidth={1.8} data-id="element-6778" data-mp="p4exr5cw2ssa" />
        <ArrowDownIcon className="md:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-[#4F46E5] bg-white" strokeWidth={1.6} data-id="element-6779" data-mp="1csy07499cbcg" />
      </div>

      <div data-id="element-6780" data-mp="1rhhk8javceme">
        <p className="font-serif text-[5rem] lg:text-[7rem] leading-none tracking-tight text-[#4F46E5]" data-id="element-6781" data-mp="1rfwt73nrjdta">{after.value}</p>
        <p className="mt-2 font-sans text-[15px] font-medium text-[#121A3A]" data-id="element-6782" data-mp="1miwkekzj3wyh">{pick(after.label)}</p>
        <p className="font-sans text-[15px] text-[#4F46E5]" data-id="element-6783" data-mp="1hh7aj8o1lpbz">{pick(after.sub)}</p>
        <p className="mt-3 font-sans text-[13px] font-semibold text-[#121A3A]" data-id="element-6784" data-mp="1cj5qolisllzg">{pick(afterNote)}</p>
      </div>
    </div>;
}

/* Five domains resting on one shared foundation */
const NODE_POS: Record<string, {
  x: number;
  y: number;
}> = {
  identity: {
    x: 50,
    y: 9
  },
  communications: {
    x: 89,
    y: 36
  },
  data: {
    x: 76,
    y: 84
  },
  systems: {
    x: 24,
    y: 84
  },
  services: {
    x: 11,
    y: 36
  }
};
export function ConnectedSystems() {
  const pick = usePick();
  return <div className="relative mx-auto w-full max-w-[440px] aspect-square" role="img" aria-label={act2.nodes.map(n => pick(n.label)).join(', ')} data-id="element-6785" data-mp="ouw7zsgvlecj">
      <div className="absolute inset-[16%] rounded-full border border-[#ECEBFA]" aria-hidden="true" data-id="element-6786" data-mp="ktks4ent07hj" />
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden="true" data-id="element-6787" data-mp="2wfv22jxqy2ok">
        {act2.nodes.map((n, i) => {
        const p = NODE_POS[n.id];
        const next = NODE_POS[act2.nodes[(i + 1) % act2.nodes.length].id];
        return <g key={n.id} data-id="element-6788" data-mp="3aeuodkxse5hk" data-mpi={i}>
              <line x1="50" y1="50" x2={p.x} y2={p.y} stroke="#4F46E5" strokeOpacity="0.35" strokeWidth="0.35" data-id="element-6789" data-mp="3af4gbe2e6mpw" />
              <line x1={p.x} y1={p.y} x2={next.x} y2={next.y} stroke="#C7C3F6" strokeWidth="0.25" strokeDasharray="1 1.2" data-id="element-6790" data-mp="2cq2ipo90e18i" />
            </g>;
      })}
      </svg>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[34%] aspect-square rounded-full bg-[#F6F5FF] border border-[#DCD9FA] flex items-center justify-center px-3 text-center" data-id="element-6791" data-mp="2mpjmg04pa51u">
        <span className="font-sans text-[11px] sm:text-[12px] font-semibold tracking-[0.12em] uppercase text-[#4F46E5] leading-tight" data-id="element-6792" data-mp="1u14iq9r0r95t">{pick(act2.foundationLabel)}</span>
      </div>
      {act2.nodes.map(n => {
      const p = NODE_POS[n.id];
      return <span key={n.id} className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-[#E4E3F2] bg-white px-3.5 py-1.5 font-sans text-[13px] font-medium text-[#121A3A] shadow-[0_6px_18px_-14px_rgba(18,26,58,0.5)]" style={{
        left: `${p.x}%`,
        top: `${p.y}%`
      }} data-id="element-6793" data-mp="jq4sfwzvf9s">
            {pick(n.label)}
          </span>;
    })}
    </div>;
}

/* Language + intention meets identity + context → ATNA */
export function AtnaConvergence() {
  const pick = usePick();
  const {
    llm,
    foundation
  } = act4;
  return <div data-id="element-6794" data-mp="s9l6zd5yazfv">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-stretch gap-5 md:gap-8" data-id="element-6795" data-mp="s9uwz0qzx6lk">
        <div className="rounded-2xl border border-[#E4E3F2] bg-white/80 px-7 py-7" data-id="element-6796" data-mp="2yzrdoxduf9w9">
          <p className="font-sans text-[12px] font-semibold tracking-[0.18em] uppercase text-[#4F46E5]" data-id="element-6797" data-mp="2u1pwyj3w3mny">{pick(llm.label)}</p>
          <p className="mt-3 font-serif text-[1.45rem] lg:text-[1.6rem] leading-snug text-[#121A3A]" data-id="element-6798" data-mp="19kgpmx78in51">{pick(llm.text)}</p>
        </div>
        <div className="flex items-center justify-center" aria-hidden="true" data-id="element-6799" data-mp="1ekmk0l4w2nqt">
          <span className="w-10 h-10 rounded-full border border-[#DCD9FA] bg-white flex items-center justify-center text-[#4F46E5]" data-id="element-6800" data-mp="10qltufs06922">
            <PlusIcon className="w-4 h-4" strokeWidth={2} data-id="element-6801" data-mp="14qchiam8g92d" />
          </span>
        </div>
        <div className="rounded-2xl border border-[#E4E3F2] bg-white/80 px-7 py-7" data-id="element-6802" data-mp="qv0ny6jtcon1">
          <p className="font-sans text-[12px] font-semibold tracking-[0.18em] uppercase text-[#4F46E5]" data-id="element-6803" data-mp="vtbw8cugmr35">{pick(foundation.label)}</p>
          <p className="mt-3 font-sans text-[15px] text-[#5A6178]" data-id="element-6804" data-mp="ltuta36lerav">{pick(foundation.lead)}</p>
          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-6805" data-mp="lvnxlbtqgzfp">
            {foundation.items.map(it => <li key={it.fr} className="rounded-full bg-[#F4F3FE] px-3 py-1 font-sans text-[13px] font-medium text-[#121A3A]" data-id="element-6806" data-mp="2g7vpegard079">{pick(it)}</li>)}
          </ul>
        </div>
      </div>
      <div className="flex flex-col items-center" aria-hidden="true" data-id="element-6807" data-mp="3p2g3j0uj1hdr">
        <span className="h-10 w-px bg-[#C7C3F6]" data-id="element-6808" data-mp="3k468cq8sygs5" />
        <ArrowDownIcon className="-mt-1 w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-6809" data-mp="3g3uqfl7tqukd" />
      </div>
      <div className="mt-3 flex justify-center" data-id="element-6810" data-mp="3b5ay932n0mjk">
        <div className="relative w-40 h-40 rounded-full bg-white border border-[#DCD9FA] shadow-[0_22px_60px_-30px_rgba(79,70,229,0.75)] flex items-center justify-center" data-id="element-6811" data-mp="3b6m4d9jk26du">
          <span className="absolute -inset-4 rounded-full border border-[#E4E3F2]" aria-hidden="true" data-id="element-6812" data-mp="7o2n122f0kxt" />
          <span className="absolute -inset-9 rounded-full border border-[#EEEDFB]" aria-hidden="true" data-id="element-6813" data-mp="2wkiy553i48m" />
          <img src={LOGO_ATNA} alt="ATNA" className="w-28 h-auto object-contain" data-id="element-6814" data-mp="3u2u8ga0u0b7k" />
        </div>
      </div>
    </div>;
}
const MARKER_ICONS = {
  team: UsersIcon,
  clients: HeartIcon,
  partners: HandshakeIcon,
  ambition: TrendingUpIcon
};
export function TeamMarkers() {
  const pick = usePick();
  return <ul className="flex flex-wrap gap-x-8 gap-y-3" data-id="element-6815" data-mp="xzmr41vf9b9w">
      {act5.markers.map(m => {
      const Icon = MARKER_ICONS[m.id];
      return <li key={m.id} className="flex items-center gap-2" data-id="element-6816" data-mp="17wirfkb62r3d">
            <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-6817" data-mp="17uxy1y0l9e73" />
            <span className="font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#5A6178]" data-id="element-6818" data-mp="1byvz4e9h4z2s">{pick(m.label)}</span>
          </li>;
    })}
    </ul>;
}